import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
  alpha,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useHistory } from "react-router-dom";

// Icons
import StorefrontIcon from "@mui/icons-material/Storefront";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SaveIcon from "@mui/icons-material/Save";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

// Components
import InputField from "../../../FormComponents/InputField";
import CustomerSelectingPopup from "../../../components/CustomerSelectingPopup";
import Ex_ProductSelectingPopup from "../../../components/Ex_ProductSelectingPopup";
import SaleBillItemForm from "./SaleBillItemForm";
import FormAlert from "../../../components/FormAlert";
import CustomerDataComponent from "../../../components/CustomerDataComponent";
import { addSaleBill } from "../../../services/Sales";
import { getCustomerById } from "../../../services/Customer";
import { getExportProductById } from "../../../services/Export";
import { getWarehouseData } from "../../../services/Warehouse";
import { getUser, getUserType } from "../../../Utils/Common";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
  },
  billTypeCard: {
    padding: theme.spacing(4),
    cursor: "pointer",
    transition: "all 0.3s ease",
    border: "2px solid transparent",
    height: "100%",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
    },
  },
  selectedCard: {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
  warehouseCard: {
    padding: theme.spacing(2.5),
    cursor: "pointer",
    transition: "all 0.2s ease",
    border: "2px solid transparent",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 16px rgba(0,0,0,0.12)",
    },
  },
  selectedWarehouseCard: {
    borderColor: theme.palette.success.main,
    backgroundColor: alpha(theme.palette.success.main, 0.05),
  },
  iconContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(3),
  },
}));

const isStaffUser = () => getUserType() === "STAFF_USER";

function SaleAddBill({ setOpenForm }) {
  const classes = useStyles();
  const history = useHistory();

  // Step control
  const [billType, setBillType] = useState("");
  const [showWarehouseStep, setShowWarehouseStep] = useState(false);
  const [showBillForm, setShowBillForm] = useState(false);

  // Warehouse state (only used for STAFF_USER)
  const [warehouses, setWarehouses] = useState([]);
  const [warehousesLoading, setWarehousesLoading] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);

  // Bill values
  const [billValues, setBillValues] = useState({
    _billType: "",
    _customer: null,
    _customerId: null,
    _note: "",
    _addedBy: getUser().email,
  });

  // Product management
  const [productRows, setProductRows] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [currentProductRow, setCurrentProductRow] = useState(null);

  // Customer management
  const [selectedCustomer, setSelectedCustomer] = useState([]);
  const [selectedCustomerData, setSelectedCustomerData] = useState(null);

  // Popup states
  const [openCustomerPopup, setOpenCustomerPopup] = useState(false);
  const [openProductPopup, setOpenProductPopup] = useState(false);
  const [openProductProcess, setOpenProductProcess] = useState(false);

  // Alert state
  const [displayAlert, setAlert] = useState(false);
  const [alertData, setAlertData] = useState({ type: "", message: "" });

  const [isLoading, setIsLoading] = useState(false);

  // Load warehouses when entering the warehouse step
  useEffect(() => {
    if (showWarehouseStep) {
      loadWarehouses();
    }
  }, [showWarehouseStep]);

  const loadWarehouses = async () => {
    setWarehousesLoading(true);
    const result = await getWarehouseData(0, 100, {
      _name: "",
      _type: "",
      _isActive: "true",
    });
    if (result && result !== "Something went wrong..." && result.content) {
      setWarehouses(result.content);
    }
    setWarehousesLoading(false);
  };

  const handleBillTypeSelect = (type) => {
    setBillType(type);
    setBillValues({ ...billValues, _billType: type });

    if (isStaffUser()) {
      setShowWarehouseStep(true);
    } else {
      setShowBillForm(true);
    }
  };

  const handleWarehouseSelect = (warehouse) => {
    setSelectedWarehouse(warehouse);
  };

  const handleWarehouseContinue = () => {
    if (!selectedWarehouse) return;
    setShowWarehouseStep(false);
    setShowBillForm(true);
  };

  const handleBack = () => {
    if (showBillForm) {
      if (isStaffUser()) {
        setShowBillForm(false);
        setShowWarehouseStep(true);
      } else {
        resetToStart();
      }
      return;
    }
    if (showWarehouseStep) {
      setShowWarehouseStep(false);
      setBillType("");
      setBillValues({ ...billValues, _billType: "" });
      setSelectedWarehouse(null);
      return;
    }
    resetToStart();
  };

  const resetToStart = () => {
    setShowBillForm(false);
    setShowWarehouseStep(false);
    setBillType("");
    setBillValues({
      _billType: "",
      _customer: null,
      _customerId: null,
      _note: "",
      _addedBy: getUser().email,
    });
    setProductRows([]);
    setSelectedCustomer([]);
    setSelectedCustomerData(null);
    setSelectedProducts([]);
    setSelectedWarehouse(null);
  };

  const handleChange = (name, val) => {
    setBillValues({ ...billValues, [name]: val });
  };

  const handleCustomerSelect = (customers) => {
    setSelectedCustomer(customers);
  };

  const handleOpenCustomerTable = () => {
    setOpenCustomerPopup(true);
  };

  const handleCloseCustomerTable = async () => {
    if (selectedCustomer.length !== 0) {
      const customerResponse = await getCustomerById(selectedCustomer[0]);
      if (customerResponse === "Something went wrong...") {
        setAlertData({ type: "error", message: "Failed to load customer data" });
        setAlert(true);
        setOpenCustomerPopup(false);
        return;
      }
      setSelectedCustomerData(customerResponse);
      setBillValues({
        ...billValues,
        _customer: customerResponse,
        _customerId: customerResponse.customerId,
      });
      setOpenCustomerPopup(false);
    } else {
      setOpenCustomerPopup(false);
    }
  };

  const handleCloseProductTable = async () => {
    if (selectedProducts.length === 0) {
      setOpenProductPopup(false);
      return;
    }

    const newProductRows = [];
    for (let i = 0; i < selectedProducts.length; i++) {
      const productId = selectedProducts[i];
      const exists = productRows.find((row) => row.productId === productId);
      if (exists) continue;

      try {
        const productDataResponse = await getExportProductById(productId);
        if (productDataResponse && productDataResponse !== "Something went wrong...") {
          newProductRows.push({
            productId: productDataResponse.product_id,
            productName: productDataResponse.name,
            barcode: productDataResponse.barcode,
            sellingQty: 0,
            unitPrice: 0,
            totalPrice: 0,
            totalCost: 0,
            totalProfit: 0,
            profitMargin: 0,
            batches: [],
          });
        } else {
          setAlertData({ type: "error", message: `Failed to load product data for ID: ${productId}` });
          setAlert(true);
        }
      } catch (error) {
        setAlertData({ type: "error", message: `Error loading product: ${error.message}` });
        setAlert(true);
      }
    }

    if (newProductRows.length > 0) {
      setProductRows([...productRows, ...newProductRows]);
    }
    setOpenProductPopup(false);
  };

  const handleOpenProductProcess = (product) => {
    setCurrentProductRow(product);
    setOpenProductProcess(true);
  };

  const handleRemoveProduct = (productId) => {
    setProductRows(productRows.filter((row) => row.productId !== productId));
  };

  const closeAlert = () => {
    setAlert(false);
    setAlertData({ type: "", message: "" });
  };

  const calculateTotals = () => {
    const totalAmount = productRows.reduce((sum, row) => sum + row.totalPrice, 0);
    const totalProfit = productRows.reduce((sum, row) => sum + parseFloat(row.totalProfit || 0), 0);
    const totalCost = productRows.reduce((sum, row) => sum + parseFloat(row.totalCost || 0), 0);
    return { totalAmount, totalProfit, totalCost };
  };

  const validateBill = () => {
    if (!billValues._customer) {
      setAlertData({ type: "error", message: "Please select a customer" });
      setAlert(true);
      return false;
    }
    if (productRows.length === 0) {
      setAlertData({ type: "error", message: "Please add at least one product to the bill" });
      setAlert(true);
      return false;
    }
    const invalidProducts = productRows.filter(
      (row) => !row.sellingQty || row.sellingQty <= 0 || !row.batches.length
    );
    if (invalidProducts.length > 0) {
      setAlertData({
        type: "error",
        message: "Please configure all products with valid quantities and production batches",
      });
      setAlert(true);
      return false;
    }
    return true;
  };

  const handleSaveBill = async () => {
    if (!validateBill()) return;

    setIsLoading(true);
    closeAlert();

    try {
      const { totalAmount, totalProfit, totalCost } = calculateTotals();
      const totalDiscount = productRows.reduce((sum, row) => sum + parseFloat(row.discountAmount || 0), 0);

      const billData = {
        billNumber: "",
        customerId: billValues._customerId,
        totalPrice: totalAmount,
        totalProfit: totalProfit,
        discount_percentage: 0,
        discount_amount: totalDiscount,
        addedBy: billValues._addedBy,
        note: billValues._note || "",
        billCategory: billValues._billType,
        remainingAmount: totalAmount,
        warehouseId: selectedWarehouse ? selectedWarehouse.warehouseId : null,
        billItems: productRows.map((row) => ({
          productId: row.productId,
          bill_qty: row.sellingQty,
          discount_perItem: row.discountAmount || 0,
          pricePerItem: row.unitPrice,
          costPerItem: row.totalCost / row.sellingQty,
          price: row.totalPrice,
          itemProfit: row.totalProfit,
          profitMargin: row.profitMargin,
          note: "",
          saleProductionItems: row.batches.map((batch) => ({
            productionId: batch.productionId,
            usingQty: batch.saleQty,
          })),
        })),
        payments: [],
      };

      const result = await addSaleBill(billData);
      if (result && result !== "Something went wrong...") {
        setAlertData({ type: "success", message: "Bill created successfully as DRAFT!" });
        setAlert(true);
        setTimeout(() => setOpenForm(false), 2000);
      } else {
        setAlertData({ type: "error", message: "Failed to save bill. Please try again." });
        setAlert(true);
      }
    } catch (error) {
      setAlertData({ type: "error", message: "An error occurred while saving the bill." });
      setAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value) => `Rs. ${parseFloat(value || 0).toFixed(2)}`;

  // ── Step 1: Bill Type Selection ──────────────────────────────────────────────
  if (!showWarehouseStep && !showBillForm) {
    return (
      <Paper className={classes.container} elevation={3}>
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={() => setOpenForm(false)} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight="600" color="primary.main">
                Create New Sale Bill
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose the type of sale bill you want to create
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Grid container spacing={4}>
          {/* Retail Bill Card */}
          <Grid item xs={12} md={6}>
            <Card
              className={`${classes.billTypeCard} ${billType === "RETAIL" ? classes.selectedCard : ""}`}
              onClick={() => handleBillTypeSelect("RETAIL")}
              elevation={billType === "RETAIL" ? 8 : 2}
            >
              <CardContent>
                <Box className={classes.iconContainer}>
                  <StorefrontIcon
                    sx={{ fontSize: 100, color: billType === "RETAIL" ? "primary.main" : "text.secondary" }}
                  />
                </Box>
                <Typography variant="h4" align="center" fontWeight="700" gutterBottom
                  color={billType === "RETAIL" ? "primary.main" : "text.primary"}>
                  Retail Sale
                </Typography>
                <Typography variant="body1" color="text.secondary" align="center" paragraph>
                  Direct sales to individual customers
                </Typography>
                <Divider sx={{ my: 3 }} />
                <Stack spacing={1.5}>
                  {["Single unit sales", "Retail pricing", "Individual customer records"].map((text) => (
                    <Box key={text} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "primary.main" }} />
                      <Typography variant="body2">{text}</Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Wholesale Bill Card */}
          <Grid item xs={12} md={6}>
            <Card
              className={`${classes.billTypeCard} ${billType === "WHOLESALE" ? classes.selectedCard : ""}`}
              onClick={() => handleBillTypeSelect("WHOLESALE")}
              elevation={billType === "WHOLESALE" ? 8 : 2}
            >
              <CardContent>
                <Box className={classes.iconContainer}>
                  <BusinessIcon
                    sx={{ fontSize: 100, color: billType === "WHOLESALE" ? "secondary.main" : "text.secondary" }}
                  />
                </Box>
                <Typography variant="h4" align="center" fontWeight="700" gutterBottom
                  color={billType === "WHOLESALE" ? "secondary.main" : "text.primary"}>
                  Wholesale Sale
                </Typography>
                <Typography variant="body1" color="text.secondary" align="center" paragraph>
                  Bulk sales to businesses and resellers
                </Typography>
                <Divider sx={{ my: 3 }} />
                <Stack spacing={1.5}>
                  {["Bulk quantity sales", "Wholesale pricing", "Business-to-business records"].map((text) => (
                    <Box key={text} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "secondary.main" }} />
                      <Typography variant="body2">{text}</Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => setOpenForm(false)}>
            Cancel
          </Button>
        </Box>
      </Paper>
    );
  }

  // ── Step 2: Warehouse Selection (STAFF_USER only) ────────────────────────────
  if (showWarehouseStep) {
    return (
      <Paper className={classes.container} elevation={3}>
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={handleBack} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight="600" color="primary.main">
                Select Warehouse
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose the warehouse for this {billType === "RETAIL" ? "Retail" : "Wholesale"} sale bill
              </Typography>
            </Box>
            <Chip label={billType} size="small" color={billType === "RETAIL" ? "primary" : "secondary"} />
          </Stack>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {warehousesLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : warehouses.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <WarehouseIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No active warehouses found
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {warehouses.map((warehouse) => {
              const isSelected = selectedWarehouse?.warehouseId === warehouse.warehouseId;
              return (
                <Grid item xs={12} sm={6} md={4} key={warehouse.warehouseId}>
                  <Card
                    className={`${classes.warehouseCard} ${isSelected ? classes.selectedWarehouseCard : ""}`}
                    onClick={() => handleWarehouseSelect(warehouse)}
                    elevation={isSelected ? 6 : 2}
                  >
                    <CardContent>
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                        <WarehouseIcon
                          sx={{ fontSize: 32, color: isSelected ? "success.main" : "text.secondary" }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight="600"
                            color={isSelected ? "success.main" : "text.primary"}>
                            {warehouse.warehouseName}
                          </Typography>
                          {warehouse.warehouseType && (
                            <Chip
                              label={warehouse.warehouseType}
                              size="small"
                              variant="outlined"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </Box>
                        {isSelected && (
                          <Chip label="Selected" size="small" color="success" />
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="outlined" onClick={handleBack}>
            Back
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={!selectedWarehouse}
            onClick={handleWarehouseContinue}
          >
            Continue
          </Button>
        </Box>
      </Paper>
    );
  }

  // ── Step 3: Bill Form ────────────────────────────────────────────────────────
  const { totalAmount, totalProfit, totalCost } = calculateTotals();
  const getBillTypeColor = () => (billType === "RETAIL" ? "primary" : "secondary");
  const getBillTypeIcon = () =>
    billType === "RETAIL" ? <StorefrontIcon fontSize="large" /> : <BusinessIcon fontSize="large" />;

  return (
    <Paper className={classes.container} elevation={3}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton onClick={handleBack} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ color: `${getBillTypeColor()}.main` }}>{getBillTypeIcon()}</Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight="600" color={`${getBillTypeColor()}.main`}>
              {billType === "RETAIL" ? "Retail" : "Wholesale"} Sale Bill
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Chip label={billType} size="small" color={getBillTypeColor()} />
              {selectedWarehouse && (
                <Chip
                  icon={<WarehouseIcon />}
                  label={selectedWarehouse.warehouseName}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {displayAlert && (
        <Box sx={{ mb: 3 }}>
          <FormAlert type={alertData.type} message={alertData.message} />
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Customer Selection */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight="600" gutterBottom sx={{ mb: 1.5 }}>
            Customer Information
          </Typography>
          {selectedCustomerData ? (
            <CustomerDataComponent
              customer={selectedCustomerData}
              onEdit={handleOpenCustomerTable}
              showEditButton={true}
            />
          ) : (
            <Card
              elevation={2}
              sx={{ cursor: "pointer", transition: "all 0.2s", "&:hover": { boxShadow: 4, transform: "translateY(-2px)" } }}
              onClick={handleOpenCustomerTable}
            >
              <CardContent sx={{ textAlign: "center", py: 4 }}>
                <ShoppingCartIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  Click to Select Customer
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Bill Summary */}
        {productRows.length > 0 && (
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight="600" gutterBottom sx={{ mb: 1.5 }}>
              Bill Summary
            </Typography>
            <Card elevation={2} sx={{ bgcolor: alpha("#4caf50", 0.05) }}>
              <CardContent>
                <Stack spacing={2}>
                  {[
                    { label: "Total Cost", value: totalCost, color: "inherit" },
                    { label: "Total Amount", value: totalAmount, color: "primary.main" },
                    { label: "Total Profit", value: totalProfit, color: "success.main" },
                  ].map(({ label, value, color }) => (
                    <React.Fragment key={label}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body2" color="text.secondary">{label}</Typography>
                        <Typography variant="h6" fontWeight="600" color={color}>
                          {formatCurrency(value)}
                        </Typography>
                      </Box>
                      <Divider />
                    </React.Fragment>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Products Section */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="600">
                  Products ({productRows.length})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<ShoppingCartIcon />}
                  onClick={() => setOpenProductPopup(true)}
                  size="small"
                  color={getBillTypeColor()}
                >
                  Add Products
                </Button>
              </Box>

              {productRows.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(billType === "RETAIL" ? "#1976d2" : "#9c27b0", 0.1) }}>
                        <TableCell><strong>Product</strong></TableCell>
                        <TableCell><strong>Barcode</strong></TableCell>
                        <TableCell align="right"><strong>Qty</strong></TableCell>
                        <TableCell align="right"><strong>Unit Price</strong></TableCell>
                        <TableCell align="right"><strong>Subtotal</strong></TableCell>
                        <TableCell align="right"><strong>Discount</strong></TableCell>
                        <TableCell align="right"><strong>Total</strong></TableCell>
                        <TableCell align="right"><strong>Profit</strong></TableCell>
                        <TableCell align="center"><strong>Status</strong></TableCell>
                        <TableCell align="center"><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productRows.map((row) => (
                        <TableRow
                          key={row.productId}
                          sx={{ "&:hover": { bgcolor: alpha(billType === "RETAIL" ? "#1976d2" : "#9c27b0", 0.05) } }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">{row.productName}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={row.barcode} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="right">
                            {row.sellingQty > 0
                              ? <Chip label={row.sellingQty} size="small" color={getBillTypeColor()} />
                              : <Chip label="Not set" size="small" color="default" />}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {row.unitPrice > 0 ? formatCurrency(row.unitPrice) : "-"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {row.subtotal > 0 ? formatCurrency(row.subtotal) : "-"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {row.discountAmount > 0 ? (
                              <Box>
                                <Typography variant="body2" color="error.main" fontWeight="600">
                                  -{formatCurrency(row.discountAmount)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ({row.discountType === "PERCENTAGE" ? `${row.discountValue}%` : `$${row.discountValue}`})
                                </Typography>
                              </Box>
                            ) : "-"}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="600">
                              {row.totalPrice > 0 ? formatCurrency(row.totalPrice) : "-"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {row.totalProfit > 0
                              ? <Typography variant="body2" fontWeight="600" color="success.main">
                                  {formatCurrency(row.totalProfit)}
                                </Typography>
                              : "-"}
                          </TableCell>
                          <TableCell align="center">
                            {row.sellingQty > 0 && row.batches.length > 0
                              ? <Chip label="Ready" size="small" color="success" />
                              : <Chip label="Pending" size="small" color="warning" />}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Configure">
                              <IconButton size="small" color={getBillTypeColor()} onClick={() => handleOpenProductProcess(row)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove">
                              <IconButton size="small" color="error" onClick={() => handleRemoveProduct(row.productId)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ textAlign: "center", py: 6, bgcolor: alpha("#000", 0.02), borderRadius: 2 }}>
                  <ShoppingCartIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">No products added yet</Typography>
                  <Typography variant="caption" color="text.secondary">Click "Add Products" to begin</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Notes */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight="600" gutterBottom sx={{ mb: 1.5 }}>
            Additional Notes (Optional)
          </Typography>
          <InputField
            name="_note"
            value={billValues._note}
            onChange={(event, newInputValue) => handleChange(event, newInputValue)}
            type="text"
            placeholder="Add any special instructions or notes for this bill..."
            multiline
            rows={3}
          />
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Divider sx={{ mb: 3 }} />
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={handleBack} disabled={isLoading}>
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveBill}
              disabled={isLoading || productRows.length === 0 || !billValues._customer}
              color={getBillTypeColor()}
            >
              {isLoading ? "Saving..." : "Save as Draft"}
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {/* Popups */}
      {openCustomerPopup && (
        <CustomerSelectingPopup
          setOpenCustomerTable={setOpenCustomerPopup}
          openCustomerTable={openCustomerPopup}
          handleCloseCustomerTable={handleCloseCustomerTable}
          setSelectedCustomer={handleCustomerSelect}
          selectedCustomer={selectedCustomer}
          isOneChoise={true}
        />
      )}

      {openProductPopup && (
        <Ex_ProductSelectingPopup
          setOpenProductTable={setOpenProductPopup}
          openProductTable={openProductPopup}
          handleCloseProductTable={handleCloseProductTable}
          setSelectedProduct={setSelectedProducts}
          selectedProduct={selectedProducts}
          filterWarehouse={selectedWarehouse}
          isOneChoise={false}
        />
      )}

      {openProductProcess && currentProductRow && (
        <SaleBillItemForm
          productRow={currentProductRow}
          setProductRows={setProductRows}
          handleOpenProductProcess={() => setOpenProductProcess(false)}
          billType={billType}
        />
      )}
    </Paper>
  );
}

export default SaleAddBill;
